import { captureException } from '@sentry/browser'
import { useState, useEffect } from 'react'
import { LEADFEED_DEFAULT_RADIUS } from '../../../../Constants'
import { LocationAttributes, Coordinates } from '../../../../Types/Location'
import { fetchLocationByCoordinates, getCurrencyByCountry } from '../../../../Utilities/Location'
import fetchLocationByAddress from '../../../../Utilities/Location/fetchLocationByAddress'
import { GeocodeResponseError } from '../../../../Utilities/Location/GoogleAPI/fetchGeocoderLocations'

interface UseLocationAttributesArgs extends Partial<Coordinates> {
  location?: string
}

type FetchLocationArgs = Coordinates | { location: string }

interface LocationAttributesState {
  fetchLoading: boolean
  geocodeError?: GeocodeResponseError
  locationAttrs?: LocationAttributes
}

const useLocationAttributes = ({
  lat,
  lon,
  location,
}: UseLocationAttributesArgs): LocationAttributesState => {
  const triggerFetchByCoordinates = lat !== undefined && lon !== undefined
  const triggerFetchByLocation = location !== undefined
  const triggerFetch = triggerFetchByCoordinates || triggerFetchByLocation
  const [fetchLoading, setFetchLoading] = useState(triggerFetch)
  const [geocodeError, setGeocodeError] = useState<GeocodeResponseError>()
  const [locationAttrs, setLocationAttrs] = useState<LocationAttributes>()

  const fetchLocation = (args: FetchLocationArgs) => {
    setFetchLoading(true)

    const locationResult =
      'location' in args
        ? fetchLocationByAddress(args.location)
        : fetchLocationByCoordinates({ lat: args.lat, lon: args.lon })

    locationResult
      .then((locationAttrs) => {
        if (locationAttrs === undefined) return setLocationAttrs(undefined)

        setLocationAttrs({
          ...locationAttrs,
          radius: LEADFEED_DEFAULT_RADIUS,
          currency: getCurrencyByCountry(locationAttrs.country),
        })
      })
      .catch((e) => {
        if (e instanceof GeocodeResponseError) {
          setGeocodeError(e)
          console.error(e.message)
          return captureException(e)
        }
        throw e
      })
      .finally(() => setFetchLoading(false))
  }

  useEffect(() => {
    if (!triggerFetch) return

    setGeocodeError(undefined)

    if (triggerFetchByCoordinates) return fetchLocation({ lat, lon })
    if (triggerFetchByLocation) return fetchLocation({ location })
  }, [lat, lon, location])

  return {
    fetchLoading,
    geocodeError,
    locationAttrs: geocodeError === undefined ? locationAttrs : undefined,
  }
}

export default useLocationAttributes
